import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiArrowRight } from 'react-icons/fi';
import { useAppContext } from '@/context/AppContext';
import { categories } from '@/data/categories';
import { templates } from '@/data/templates';
import { applyTemplateToDraft } from '@/utils/templateEngine';
import logger from '@/utils/logger';
import Badge from '@/components/Badge';
import SectionTitle from '@/components/SectionTitle';

const TemplateCard = ({ template, onSelect }) => {
  const categoryLabel = categories.find(c => c.id === template.applyTo)?.label || 'General';

  return (
    <motion.div
      whileHover={{ y: -5, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}
      className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col"
    >
      <div className="p-6 flex-grow">
        <Badge>{categoryLabel}</Badge>
        <h3 className="text-xl font-semibold text-gray-800 mt-3">{template.name}</h3>
        <p className="text-gray-500 mt-2 text-sm">{template.description}</p>
      </div>
      <div className="p-6 bg-gray-50">
        <button
          onClick={() => onSelect(template)}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Use Template
          <FiArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
};

function Categories() {
  const { projectDraft, dispatch } = useAppContext();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleSelectTemplate = (template) => {
    const newDraft = applyTemplateToDraft(template, projectDraft);
    dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: newDraft });
    logger.success(`Template "${template.name}" applied.`, { templateId: template.id });
    navigate('/start-building');
  };

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.applyTo === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <SectionTitle title="Project Templates" subtitle="Choose a template to get started quickly." />
      
      <div className="flex flex-wrap gap-4 mb-8">
        <div className="relative flex-grow">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          className="border-gray-300 rounded-md"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.label}</option>
          ))}
        </select>
      </div>

      {filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTemplates.map(template => (
            <TemplateCard key={template.id} template={template} onSelect={handleSelectTemplate} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
            <p className="text-gray-500">No templates found for your criteria.</p>
        </div>
      )}
    </motion.div>
  );
}

export default Categories;